import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import BackgroundContainer from '../components/BackgroundContainer';
import BaseHeader from '../components/BaseHeader';
import BaseButton from '../components/BaseButton';
import BaseFormContainer from "../components/BaseFormContainer";

const ScrollableContent = styled(Box)(({ theme }) => ({
  overflowY: "auto",
  paddingRight: theme.spacing(1),
  flex: 1,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 191, 229, 0.4)',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
  },
}));

const StyledRule = styled(Typography)(({ theme }) => ({
  fontFamily: 'Playfair Display, serif',
  fontSize: '1.2rem',
  color: '#ffbfe1',
  lineHeight: 1.5,
  marginBottom: theme.spacing(2.5),
  textShadow: '0px 2px 6px rgba(0, 0, 0, 0.5)',
  borderBottom: '1px solid rgba(255, 191, 229, 0.1)',
  paddingBottom: theme.spacing(1),
  '@media (max-width: 400px)': {
    fontSize: '1.1rem',
  },
  '@media (max-width: 360px)': {
    fontSize: '1rem',
  },
  '@media (max-width: 320px)': {
    fontSize: '0.95rem',
  },
}));

const ModeTab = styled(Box)(({ active }) => ({
  flex: 1,
  textAlign: 'center',
  fontFamily: 'Cormorant Garamond, serif',
  fontWeight: 600,
  fontSize: '1rem',
  letterSpacing: '0.08em',
  padding: '10px 24px',
  cursor: 'pointer',
  borderRadius: '50px',
  WebkitTapHighlightColor: 'transparent',
  outline: 'none',
  transition: 'all 0.25s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: active ? '#ffbfe5' : 'rgba(194, 184, 194, 0.5)',
  background: active
    ? 'linear-gradient(180deg, rgba(72, 22, 44, 0.92) 0%, rgba(44, 10, 28, 0.96) 100%)'
    : 'transparent',
  border: active ? '1px solid rgba(175, 80, 115, 0.32)' : '1px solid transparent',
  boxShadow: active ? 'inset 0 1px 0 rgba(230, 170, 200, 0.1), 0 4px 14px rgba(0,0,0,0.4)' : 'none',
  '&:active': {
    transform: 'scale(0.96)',
  },
}));

const drinkRules = [
  "Each player takes turns choosing a card.",
  "A player may choose to declare that both players drink instead of picking a card — the turn then skips.",
  "Picking a card but not wanting to answer or perform it requires taking a shot instead.",
  "When a dare involves both players, any player who refuses must take a shot to skip it.",
  "Agreeing to a dare but failing to complete it within the given time, or giving up midway, results in a penalty shot.",
  "Stay aware of your emotional state and alcohol level.",
  "Before feeling too intoxicated, continue playing but skip taking more shots, even if the game suggests it.",
  "You can switch to Replay Mode at any time — if you've had enough to drink or want to keep playing without alcohol, just swap modes mid-game from the settings.",
];

const replayRules = [
  "Each player takes turns choosing a card.",
  "If you skip a Truth question, your partner gets to pick any truth question from the game — or come up with one of their own for you to answer. If you skip a Dare, your partner picks any previous dare played, and you must complete it for double the original time!",
  "You cannot give a player a question or a dare they refused in the past - unless something changed and they are now open to it.",
  "If you give up on a dare mid-timer, your partner picks any previous dare for you to redo instead, also for double the time!",
  "Agreeing to a dare but failing to complete it means your partner picks a replacement dare from the ones you've played.",
  "If either player feels uncomfortable at any point — even mid-dare and even if it's not their turn — they can always stop. However, they must still account for it: their partner will choose a replacement dare for them to complete instead!",
  "There's no alcohol involved — keep playing as long as you like!",
  "You can switch to Drink Mode at any time — if you decide to add some drinks to the mix, just swap modes mid-game from the settings.",
];

const GameRules = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('drink');

  const rules = mode === 'drink' ? drinkRules : replayRules;

  return (
    <BackgroundContainer>
      <BaseFormContainer sx={{ width: '80%', flex: 1, maxHeight: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <BaseHeader sx={{
          fontSize: '3.3rem',
          '@media (max-width: 480px)': { fontSize: '3rem' },
          '@media (max-width: 400px)': { fontSize: '2.7rem' },
          '@media (max-width: 360px)': { fontSize: '2.4rem' },
          paddingTop: '6px',
          marginBottom: '12px',
          textAlign: 'center',
          filter: 'drop-shadow(0 0 3px rgba(177, 39, 135, 0.36))',
        }}>
          Game Rules
        </BaseHeader>

        <Box sx={{
          display: 'flex',
          gap: '8px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '50px',
          padding: '4px',
          border: '1px solid rgba(175, 80, 115, 0.15)',
          marginBottom: '20px',
        }}>
          <ModeTab active={mode === 'drink' ? 1 : 0} onClick={() => setMode('drink')}>
            Drink Mode
          </ModeTab>
          <ModeTab active={mode === 'replay' ? 1 : 0} onClick={() => setMode('replay')}>
            Replay Mode
          </ModeTab>
        </Box>

        <ScrollableContent>
          {rules.map((rule, index) => (
            <StyledRule key={`${mode}-${index}`}>
              {index + 1}. {rule}
            </StyledRule>
          ))}
        </ScrollableContent>

        <BaseButton sx={{ marginTop: 3, alignSelf: 'center' }} onClick={() => navigate(-1)}>Back</BaseButton>
      </BaseFormContainer>
    </BackgroundContainer>
  );
};

export default GameRules;
